import { useState } from 'react';
import { Num, T, Var } from 'gt-react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { FileTextIcon } from '@phosphor-icons/react';
import type { ValidationRuleDTO, ValidationRunDTO } from '@erp/rpc';
import {
  useRegenerateValidationReview,
  useValidationRule,
  useValidationRuns,
} from '@/api/rpc/hooks/inventory-management/useValidationRules';
import Page from 'src/components/page/Page';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { VALIDATIONS_PATH } from './ValidationsPage';
import {
  RuleStatusBadge,
  formatValidationTimestamp,
} from './components/validationRuleDisplay';
import { ValidationRuleDefinitionCard } from './components/ValidationRuleDefinitionCard';
import { ValidationRuleActionsPanel } from './components/ValidationRuleActionsPanel';
import { ValidationRunHistoryTable } from './components/ValidationRunHistoryTable';
import { ValidationOpenViolationsPanel } from './components/ValidationOpenViolationsPanel';
import { ValidationRulePreviewPanel } from './components/ValidationRulePreviewPanel';
import { ValidationRuleWizardDialog } from './wizard/ValidationRuleWizard';

const ROUTE_ID = '/authenticated/item-master/validations/$ruleId';

const RUNS_PAGE_SIZE = 10;

/** One review, and how many of the loaded runs staged corrections into it. */
interface GeneratedReview {
  changeSetId: string;
  runCount: number;
  /** The most recent of those runs — the runs arrive newest first. */
  latestRunAt: string;
}

/**
 * The reviews this rule's runs staged corrections into, derived from the run
 * history already on screen. One review usually collects several runs' worth of
 * corrections, so the same id is folded rather than listed twice.
 */
function generatedReviews(runs: ValidationRunDTO[]): GeneratedReview[] {
  const reviews: GeneratedReview[] = [];
  for (const run of runs) {
    if (run.changeSetId === null) continue;
    const existing = reviews.find(
      (review) => review.changeSetId === run.changeSetId,
    );
    if (existing) {
      existing.runCount += 1;
      continue;
    }
    reviews.push({
      changeSetId: run.changeSetId,
      runCount: 1,
      latestRunAt: run.startedAt,
    });
  }
  return reviews;
}

/**
 * The reviews this rule's runs staged corrections into — the page's headline,
 * not a footnote: someone who opens a rule that has already run is usually here
 * for the review it produced. The newest review is a banner right under the
 * title; older ones (rare — a run appends to the rule's open draft) follow as
 * compact links. Derived from the first page of run history, so it does not
 * change while the history table is paged.
 */
function GeneratedReviews({
  ruleId,
  runs,
}: {
  ruleId: string;
  runs: ValidationRunDTO[];
}) {
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const { mutate: regenerate, isPending: isRegenerating } =
    useRegenerateValidationReview();
  const reviews = generatedReviews(runs);
  const [latest, ...older] = reviews;
  if (!latest) return null;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
        <FileTextIcon className="h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            <T context="banner on a validation rule's page: its runs created a review of proposed corrections">
              This rule&apos;s runs created a review
            </T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T context="how many of a validation rule's runs staged into the review, and when the latest of them ran">
              <Num>{latest.runCount}</Num> runs staged corrections · latest{' '}
              <Var>{formatValidationTimestamp(latest.latestRunAt)}</Var>
            </T>
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isRegenerating}
          onClick={() => setRegenerateDialogOpen(true)}
        >
          {isRegenerating ? (
            <T context="regenerate-review button while the run is going">
              Regenerating…
            </T>
          ) : (
            <T context="button opening the confirmation to rebuild a rule's review from scratch">
              Regenerate…
            </T>
          )}
        </Button>
        <Button asChild size="sm">
          <Link
            to="/item-master/reviews/$changeSetId"
            params={{ changeSetId: latest.changeSetId }}
          >
            <T context="button to the review a validation rule staged corrections into">
              Open review
            </T>
          </Link>
        </Button>
      </div>
      {regenerateDialogOpen && (
        <Dialog open onOpenChange={setRegenerateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                <T context="title of the dialog that rebuilds a validation rule's review">
                  Regenerate this review?
                </T>
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              <T context="explains what regenerating a validation rule's review does">
                The rule runs now and stages every open violation into a
                brand-new review, leaving exempted violations out. The current
                review is not changed or deleted — copy over any edits you made
                on it, then delete it yourself.
              </T>
            </p>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRegenerateDialogOpen(false)}
              >
                <T context="button closing the regenerate dialog without running">
                  Cancel
                </T>
              </Button>
              <Button
                type="button"
                disabled={isRegenerating}
                onClick={() => {
                  setRegenerateDialogOpen(false);
                  regenerate({ ruleId });
                }}
              >
                <T context="button confirming the rebuild of a validation rule's review">
                  Regenerate
                </T>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {older.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {older.map((review) => (
            <li key={review.changeSetId}>
              <Link
                to="/item-master/reviews/$changeSetId"
                params={{ changeSetId: review.changeSetId }}
                className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50"
              >
                <FileTextIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-primary underline-offset-2 hover:underline">
                  <T context="link to one older review a validation rule staged corrections into">
                    Open older review
                  </T>
                </span>
                <span className="text-xs text-muted-foreground">
                  <T context="how many of a validation rule's runs staged into one review, and when the latest of them ran">
                    <Num>{review.runCount}</Num> runs · latest{' '}
                    <Var>{formatValidationTimestamp(review.latestRunAt)}</Var>
                  </T>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Name, description and status — the header of a rule's page. */
function RuleHeader({ rule }: { rule: ValidationRuleDTO }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <RuleStatusBadge status={rule.status} />
      {rule.description !== null && rule.description !== '' && (
        <p className="text-sm text-muted-foreground">{rule.description}</p>
      )}
    </div>
  );
}

/**
 * One validation rule: what it checks, what running it does, what it has found,
 * and what is still open. The main column reads the rule's findings; the right
 * rail is everything that changes it.
 */
export function ValidationRuleDetailPage() {
  const navigate = useNavigate();
  const { ruleId } = useParams({ from: ROUTE_ID });
  const { rule, isLoading, error } = useValidationRule(ruleId);

  const [runsPage, setRunsPage] = useState(1);
  const [runsPageSize, setRunsPageSize] = useState(RUNS_PAGE_SIZE);
  const {
    runs,
    total: runTotal,
    isLoading: runsLoading,
  } = useValidationRuns(ruleId, { page: runsPage, pageSize: runsPageSize });

  // The newest runs, regardless of where the history table is paged — the
  // header's review banner is derived from these. While the table sits on its
  // first page this is the same cached query, not a second request.
  const { runs: latestRuns } = useValidationRuns(ruleId, {
    page: 1,
    pageSize: RUNS_PAGE_SIZE,
  });

  // Opened on the rule itself (Edit) or on a fresh duplicate of it.
  const [wizardRule, setWizardRule] = useState<ValidationRuleDTO | null>(null);

  return (
    <Page
      title={
        rule ? (
          rule.name
        ) : (
          <T context="page title, a single validation rule">Validation rule</T>
        )
      }
      isLoading={isLoading}
      error={error ?? undefined}
    >
      {rule ? (
        <div className="flex flex-col gap-2 lg:flex-row">
          <div className="min-w-0 flex-1 space-y-2">
            <RuleHeader rule={rule} />
            <GeneratedReviews ruleId={rule.id} runs={latestRuns} />
            {/*
              The run list is newest-first, so its first row is the last run —
              but only on the first page. Paged past it, we no longer know which
              run is newest, so pass `undefined` and let the card omit the row
              rather than claim the top of page 3 is the latest.
            */}
            <ValidationRuleDefinitionCard
              rule={rule}
              lastRun={runsPage === 1 ? (runs[0] ?? null) : undefined}
            />
            <ValidationRulePreviewPanel ruleId={rule.id} />
            <ValidationRunHistoryTable
              ruleId={rule.id}
              runs={runs}
              total={runTotal}
              isLoading={runsLoading}
              page={runsPage}
              pageSize={runsPageSize}
              onPageChange={setRunsPage}
              onPageSizeChange={(size) => {
                setRunsPageSize(size);
                setRunsPage(1);
              }}
            />
            <ValidationOpenViolationsPanel ruleId={rule.id} />
          </div>

          <ValidationRuleActionsPanel
            rule={rule}
            onEdit={setWizardRule}
            onDeleted={() => void navigate({ to: VALIDATIONS_PATH })}
          />
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          <T context="empty state when a validation rule id does not resolve">
            That validation rule no longer exists.
          </T>
        </p>
      )}

      <ValidationRuleWizardDialog
        open={wizardRule !== null}
        onOpenChange={(open) => {
          if (!open) setWizardRule(null);
        }}
        rule={wizardRule}
        onSaved={(savedRuleId) => {
          // A duplicate saves under a different id — follow it, so the page
          // always shows the rule the builder just wrote. Same route, so the
          // component survives the navigation: pagination has to be reset by
          // hand or the new rule opens on the old rule's page number.
          if (savedRuleId !== ruleId) {
            setRunsPage(1);
            void navigate({
              to: '/item-master/validations/$ruleId',
              params: { ruleId: savedRuleId },
            });
          }
        }}
      />
    </Page>
  );
}
